import React, { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Stack,
  Typography,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import * as XLSX from 'xlsx';
import toast from 'react-hot-toast';
import getAxiosInstance from 'api'; // adjust path based on your project structure

const ExcelUploadDialog = ({ isOpen, setIsOpen, fetchBoiReportHistory }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    const validTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
      'text/csv',
    ];

    if (!file) return;

    if (!validTypes.includes(file.type)) {
      setError('Only Excel (.xlsx, .xls) or CSV files are allowed.');
      setSelectedFile(null);
    } else {
      setError('');
      setSelectedFile(file);
    }
  };

  const handleSubmit = () => {
    if (!selectedFile) {
      setError('Please select a file.');
      return;
    }

    const reader = new FileReader();

    reader.onload = async (e) => {
      let jsonData = [];

      try {
        let workbook;
        if (selectedFile.type === 'text/csv') {
          const binaryStr = e.target.result;
          workbook = XLSX.read(binaryStr, { type: 'binary' });
        } else {
          const data = new Uint8Array(e.target.result);
          workbook = XLSX.read(data, { type: 'array' });
        }

        const worksheet = workbook.Sheets[workbook.SheetNames[0]];
        jsonData = XLSX.utils.sheet_to_json(worksheet, { defval: '' });

        if (!jsonData.length) {
          toast.error('File is empty or invalid');
          return;
        }

        const headerMap = {
          'uniqueid': 'referenceId',
          'firstname': 'firstName',
          'lastname': 'lastName',
          'emailaddress': 'email',
          'state': 'state',
          'fundtype': 'fundType',
          'link': 'pdfLink',
        };

        jsonData = jsonData.map((item) => {
          const normalizedItem = {};
          Object.entries(headerMap).forEach(([headerKey, propName]) => {
              // Find the actual key in the item (case-insensitive, ignore spaces)
            const actualKey = Object.keys(item).find(
              (k) => k.replace(/\s+/g, '').toLowerCase() === headerKey
            );
            normalizedItem[propName] = actualKey ? item[actualKey] : '';
          });
          return normalizedItem;
        });

        setLoading(true);
        const res = await getAxiosInstance(
          'businessPartner/createBulkBOI',
          'post',
          jsonData
        );

        if (res?.data?.codes === 200) {
          toast.success('The uploaded file is processing, we will notify you when the reports are ready for download via email!',{ duration: 8000 });
          setIsOpen(false);
          setSelectedFile(null);
          fetchBoiReportHistory();
        } else {
          toast.error(res?.data?.message);
        }
      } catch (err) {
        toast.error(err?.message || 'Upload error.');
      } finally {
        setLoading(false);
      }
    };

    if (selectedFile.type === 'text/csv') {
      reader.readAsBinaryString(selectedFile);
    } else {
      reader.readAsArrayBuffer(selectedFile);
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={() => setIsOpen(false)}
      PaperProps={{
        sx: {
          borderRadius: '20px',
          p: 2,
          width: '100%',
          maxWidth: 700,
          minHeight: 400,
        },
      }}
    >
      <DialogTitle>
        <Stack direction="row" justifyContent="space-between">
          <Typography variant="h4" fontWeight={600}>
            Upload File
          </Typography>
          <IconButton onClick={() => setIsOpen(false)}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={3} mt={1}>
          <Box
            sx={{
              border: '1.5px dashed #BDBDBD',
              borderRadius: '8px',
              padding: '32px',
              textAlign: 'center',
              background: '#FAFAFA',
              mb: 2,
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2,
            }}
          >
            <UploadFileIcon sx={{ fontSize: 48, color: '#F1592A' }} />

            <Typography variant="body1" textAlign="center">
              Please upload a valid Excel (.xlsx, .xls) or CSV (.csv) file
            </Typography>

            <Button variant="outlined" component="label" color="secondary">
              Select File
              <input
                type="file"
                accept=".xlsx, .xls, .csv"
                hidden
                onChange={handleFileChange}
              />
            </Button>

            {selectedFile && (
              <Typography variant="body2" color="text.primary">
                {selectedFile.name}
              </Typography>
            )}

            {error && (
              <Typography variant="body2" color="error">
                {error}
              </Typography>
            )}
          </Box>

          <Button
            variant="contained"
            fullWidth
            onClick={handleSubmit}
            disabled={loading}
            color="secondary"
            sx={{
              py: 1.2,
              borderRadius: '8px',
              fontWeight: 600,
            }}
          >
            {loading ? (
              <CircularProgress size={24} sx={{ color: '#fff' }} />
            ) : (
              'Upload File'
            )}
          </Button>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default ExcelUploadDialog;
